import posthog from "posthog-js";

const getError = (context: unknown) => {
  if (context instanceof Error) return context;

  if (
    typeof context === "object" &&
    context !== null &&
    "error" in context &&
    context.error instanceof Error
  )
    return context.error;

  return null;
};

const safeStringify = (value: unknown): string => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

type LogLevel = "info" | "warn" | "error";

class PostHogLogger {
  private static instance: PostHogLogger;

  static getInstance(): PostHogLogger {
    if (!PostHogLogger.instance) PostHogLogger.instance = new PostHogLogger();
    return PostHogLogger.instance;
  }

  private capture(level: LogLevel, message: string, context?: unknown) {
    if (typeof document === "undefined") return;

    const properties: Record<string, unknown> = {
      level,
      message,
      ...(context !== undefined ? { payload: context } : {})
    };

    posthog.capture(`log_${level}`, properties);
  }

  info(message: string, context?: unknown) {
    this.capture("info", message, context);
  }

  warn(message: string, context?: unknown) {
    this.capture("warn", message, context);
  }

  error(message: string, context?: unknown) {
    const actualError = getError(context);

    const errorInstance = actualError || new Error(safeStringify(context));

    const errorContext = {
      payload: {
        ...(typeof context === "object" &&
        context !== null &&
        !(context instanceof Error)
          ? context
          : {}),
        errorName: errorInstance.name,
        errorMessage: errorInstance.message,
        errorStack: errorInstance.stack,
        errorCause: errorInstance.cause
      }
    };

    this.capture("error", message, errorContext);
  }
}

export const logger = PostHogLogger.getInstance();
